import Vue from 'vue';
import vue from './main';
import Router from 'vue-router';
import URLS from './URLS';
import AuthService from './services/AuthService';
import AnalyticService from './services/AnalyticService';
import RestX from '~/RestX';
import UniversityService from './services/UniversityService';
import DialogService from './services/DialogService';

import i18n from '~/i18n';

Vue.use(Router);

// Project views
// import MyPage from './views/mypage/MyPage';
// import FindJobs from './views/findjobs/FindJobs';
// import JobDetail from './views/jobdetail/JobDetail';
// import FindCompanies from './views/findcompanies/FindCompanies';
// import CompanyDetail from './views/companydetail/CompanyDetail';
// import Spotlight from './views/spotlight/Spotlight';
// import Tests from './views/tests/Tests';
// import EmailVerification from './views/emailverification/EmailVerification';
// import RecoverUser from './views/recoveruser/RecoverUser';
// import RecoverPassword from './views/recoverpassword/RecoverPassword';
// import Resources from './views/resources/Resources';

const router = new Router({
    base: '/', // process.env.BASE_URL,
    mode: 'history',
    scrollBehavior(to, from, savedPosition) {
        // Check
        return { x: 0, y: 0 };
    },
    routes: [
        {
            path: URLS.mypage,
            name: 'home',
            component: () => import('./views/mypage/MyPage'),
            meta: { requiresAuth: true },
        },
        {
            path: URLS.findjobs,
            name: 'jobs',
            component: () => import('./views/findjobs/FindJobs'),
        },
        {
            path: URLS.jobDetail.concat('/:id'),
            name: 'jobdetail',
            component: () => import('./views/jobdetail/JobDetail'),
        },
        {
            path: URLS.resources,
            name: 'resources',
            component: () => import('./views/resources/Resources'),
            meta: { requiresAuth: true, requiresVerification: true },
        },
        {
            path: URLS.internalResources,
            name: 'internalresources',
            component: () => import('./views/resources_internal/overview'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
        },
        {
            path: URLS.internalResources.concat('/:id'),
            name: 'internalresources-folder',
            component: () => import('./views/resources_internal/folder'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
        },
        {
            path: URLS.calendar,
            name: 'calendar-overview',
            component: () => import('./views/calendar/overview'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
        },
        {
            path: URLS.calendar.concat(URLS.book),
            name: 'calendar-appointments-list',
            component: () => import('./views/calendar/appointment_list'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
        },
        {
            path: URLS.calendar.concat(URLS.book).concat('/:id'),
            name: 'calendar-appointments-book',
            component: () => import('./views/calendar/appointment_book'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
        },
        {
            path: URLS.findcompanies,
            name: 'companies',
            component: () => import('./views/findcompanies/FindCompanies'),
        },
        {
            path: URLS.findcompanies.concat('/:id'),
            name: 'companydetail',
            component: () => import('./views/companydetail/CompanyDetail'),
        },
        {
            path: URLS.spotlight,
            name: 'spotlight',
            component: () => import('./views/spotlight/Spotlight'),
            meta: { requiresAuth: true },
        },
        {
            path: URLS.favorites,
            name: 'favorites',
            component: () => import('./views/favorites/Favorites'),
            meta: { requiresAuth: true },
        },
        {
            path: URLS.tests,
            component: () => import('./views/tests/Tests'),
            meta: { requiresAuth: true },
            children: [
                {
                    path: '/',
                    name: 'tests-dashboard',
                    component: () => import('./views/tests/TestsDashboard'),
                    meta: { requiresAuth: true },
                },
                {
                    path: URLS.cute,
                    name: 'tests-cute',
                    component: () => import('./views/test_cute/Cute'),
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: URLS.emailVerification,
            name: 'emailVerification',
            component: () =>
                import('./views/emailverification/EmailVerification'),
        },
        {
            path: URLS.recoverUser,
            name: 'recoverUser',
            component: () => import('./views/recoveruser/RecoverUser'),
        },
        {
            path: URLS.recoverPassword,
            name: 'recoverPassword',
            component: () => import('./views/recoverpassword/RecoverPassword'),
        },
        {
            path: URLS.redirect, // Is for handling redirection-events, for example oauth-flow
            name: 'redirect',
            component: () => import('./views/redirect/Redirect'),
        },
        {
            path: URLS.aboutGPTW,
            name: 'aboutGPTW',
            component: () => import('./views/aboutGPTW/AboutGPTW'),
        },
    ],
});

router.beforeEach((to, from, next) => {
    const logInModule = RestX.getInterfaces().logInModule;
    logInModule.resetDisabledPopup();
    // Check if user is trying to leave spotlight-page with unsaved changes
    if (from.path === URLS.spotlight && AuthService.isLoggedIn()) {
        if (RestX.getInterfaces().unsavedChanges.hasUnsavedChanges()) {
            RestX.getInterfaces().unsavedChanges.setRedirect(to.path);
            RestX.getInterfaces().unsavedChanges.toggleOpen();
        } else {
            next();
        }
    } else if (to.matched.some(record => record.meta.requiresAuth)) {
        // Check if authenticated (there is a token)
        if (!AuthService.isLoggedIn()) {
            RestX.getInterfaces().logInModule.toggleOpen();
            RestX.getInterfaces().logInModule.setRedirect(to.path);
        } else {
            if (to.matched.some(record => record.meta.requiresVerification)) {
                const currentUniDomain = UniversityService.getCurrentDomain();
                RestX.getInterfaces()
                    .university.connection.get({})
                    .then(it => {
                        let currentUni = it.data.find(
                            c =>
                                (c.school.subDomain || '').toLowerCase() ===
                                currentUniDomain
                        );
                        if (currentUni) {
                            // User either is verified at this school or has a pending verification
                            if (currentUni.isVerified) {
                                next();
                            } else {
                                DialogService.openUniVerifyDialog({
                                    payload: {
                                        checkConnections: true,
                                        // TODO: FIX DIS TEXT
                                        helperText: i18n.t(
                                            'general.dialogs.helperTexts.checkHasPendingAction'
                                        ),
                                    },
                                    then: payload => {
                                        if (
                                            payload instanceof Object &&
                                            payload.isVerified
                                        ) {
                                            next();
                                        }
                                    },
                                });
                            }
                        } else {
                            // User needs to verify to continue
                            DialogService.openUniVerifyDialog({
                                payload: {
                                    checkConnections: true,
                                    // TODO: FIX DIS TEXT
                                    helperText: i18n.t(
                                        'general.dialogs.helperTexts.verifySchoolConnection'
                                    ),
                                },
                                then: payload => {
                                    if (
                                        payload instanceof Object &&
                                        payload.isVerified
                                    ) {
                                        next();
                                    }
                                },
                            });
                        }
                    });
            } else {
                next();
            }
        }
    } else {
        next(); // Always have to call next()
    }
});

router.afterEach((to, from) => {
    // Log pageviews. Pages that are just an redirect from external token-providers
    // like /redirect will not be logged. Same with /recover and /recover-password
    // because they contain sensitive data in the url-query.
    if (
        !to.path.startsWith(URLS.redirect) &&
        !to.path.startsWith(URLS.recoverUser) &&
        !to.path.startsWith(URLS.recoverPassword)
    ) {
        AnalyticService.logPageview(to.path);
    }

    RestX.getInterfaces().university.connection.get({ forceUpdate: true });

    const logInModule = RestX.getInterfaces().logInModule;
    if (logInModule.shouldOpenOnNewPage()) {
        logInModule.setHelperText(
            i18n.t('general.dialogs.helperTexts.logInToAccessMoreFeatures')
        );
        logInModule.setOpen(true);
        logInModule.setShouldOpenOnNewPage(false);
    }
});

export default router;
